import { Link } from "wouter";
import { formatUnits } from "viem";
import { useCurveStats } from "@/lib/curve-stats";
import { useCurveVolume } from "@/lib/curve-volume";
import { useMarketStats } from "@/lib/market";
import { usePools } from "@/lib/pools";

function num(n: number, d = 0): string {
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}
function usd(n: number, d = 4): string {
  return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}
// Whole-dollar USD from a plain number (e.g. market cap). "—" when missing.
function usdNum(n: number | undefined, d = 0): string {
  if (n === undefined) return "—";
  return "$" + num(n, d);
}
function usdNumCompact(n: number | undefined): string {
  if (n === undefined) return "—";
  return "$" + compactNum(n);
}
function fmtUnits(v: bigint | undefined, d = 2): string {
  if (v === undefined) return "—";
  return num(Number(formatUnits(v, 18)), d);
}
function usdUnits(v: bigint | undefined, d = 0): string {
  if (v === undefined) return "—";
  return "$" + num(Number(formatUnits(v, 18)), d);
}
// Compact phone form: 1,000 -> 1K, 1,000,000 -> 1M. Shown only on phones (see
// .lsv-short / .lsv-full in tokens.css); desktop keeps the full number.
function compactNum(n: number): string {
  return Number(n).toLocaleString("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  });
}
function compactUnits(v: bigint | undefined): string {
  if (v === undefined) return "—";
  return compactNum(Number(formatUnits(v, 18)));
}
function usdCompactUnits(v: bigint | undefined): string {
  if (v === undefined) return "—";
  return "$" + compactNum(Number(formatUnits(v, 18)));
}
// Renders both the full value (desktop) and the compact value (phone); CSS
// shows exactly one based on viewport width.
function StatValue({ full, short }: { full: string; short: string }) {
  return (
    <>
      <span className="lsv-full">{full}</span>
      <span className="lsv-short">{short}</span>
    </>
  );
}

// The persistent live stat strip. Rendered under the header on every surface
// (home, docs, dapp). Reads come from the shared useCurveStats hook.
export function StatStrip({ className }: { className?: string }) {
  const stats = useCurveStats();
  const vol = useCurveVolume();
  const market = useMarketStats();
  const pools = usePools();
  const spotNum = stats.spot !== undefined ? Number(formatUnits(stats.spot, 18)) : 0;
  const volWei = vol.data ? BigInt(vol.data.totalVolumeUsds) : undefined;
  const isBonded = stats.bonded === true;
  // Live secondary-market figures: total liquidity and 24h volume summed over
  // every 01 pool on Uniswap (real per-pool figures from the pools route, no
  // synthesis). Undefined until loaded or when no pools are listed, so the
  // stats render an honest dash instead of a fabricated $0.
  const poolList = pools.data?.pools;
  const hasPools = poolList !== undefined && poolList.length > 0;
  const dexTvl = hasPools ? poolList.reduce((sum, p) => sum + (p.tvlUsd ?? 0), 0) : undefined;
  const dexVol = hasPools
    ? poolList.reduce((sum, p) => sum + (p.volume24hUsd ?? 0), 0)
    : undefined;
  return (
    <div className={className ? `statstrip ${className}` : "statstrip"}>
      {isBonded ? (
        <>
          {/* Post-bond: "01 price" is the secondary-market price (Uniswap); the
              curve's own price is now only the buyback floor, shown alongside.
              Two stats carry ls-phone-hide so the phone grid stays three rows;
              the full set lives in the dapp Info view. */}
          <Link href="/app" className="live-stat" aria-label="01 market price on a secondary market, open the app">
            <span className="ls-label">01 price</span>
            <span className="ls-value">{market.marketPriceUsd !== null ? usd(market.marketPriceUsd) : "—"}</span>
          </Link>
          <Link href="/app" className="live-stat ls-phone-hide" aria-label="curve buyback floor price, open the app">
            <span className="ls-label">floor</span>
            <span className="ls-value">{stats.spot !== undefined ? usd(spotNum) : "—"}</span>
          </Link>
          <Link href="/app" className="live-stat" aria-label="market cap, market price times circulating supply, open the app">
            <span className="ls-label">market cap</span>
            <span className="ls-value"><StatValue full={usdNum(market.marketCapUsd)} short={usdNumCompact(market.marketCapUsd)} /></span>
          </Link>
          <Link href="/app/info" className="live-stat" aria-label="liquidity across 01 pools on Uniswap, open the app info view">
            <span className="ls-label">liquidity</span>
            <span className="ls-value"><StatValue full={usdNum(dexTvl)} short={usdNumCompact(dexTvl)} /></span>
          </Link>
          <Link href="/app/info" className="live-stat ls-phone-hide" aria-label="24 hour trading volume across 01 pools on Uniswap, open the app info view">
            <span className="ls-label">24h volume</span>
            <span className="ls-value"><StatValue full={usdNum(dexVol)} short={usdNumCompact(dexVol)} /></span>
          </Link>
          <Link href="/app" className="live-stat" aria-label="reserve in USDS, open the app">
            <span className="ls-label">reserve (USDS)</span>
            <span className="ls-value"><StatValue full={usdUnits(stats.reserve, 0)} short={usdCompactUnits(stats.reserve)} /></span>
          </Link>
          <Link href="/app" className="live-stat" aria-label="circulating supply, open the app">
            <span className="ls-label">circulating</span>
            <span className="ls-value"><StatValue full={fmtUnits(stats.totalSupply, 0)} short={compactUnits(stats.totalSupply)} /></span>
          </Link>
          {/* Once bonded nothing can ever mint again, so the burned figure is
              the hard cap minus totalSupply: every 01 the fee has burned plus
              the remainder above S_MAX that can never be minted. */}
          <Link href="/app" className="live-stat" aria-label="01 gone from possible supply, burned in trading plus the remainder under the 21 million cap that no one can ever mint, open the app">
            <span className="ls-label">burned</span>
            <span className="ls-value"><StatValue full={fmtUnits(stats.removed, 0)} short={compactUnits(stats.removed)} /></span>
          </Link>
        </>
      ) : (
        <>
          <Link href="/app" className="live-stat" aria-label="01 price, open the app">
            <span className="ls-label">01 price</span>
            <span className="ls-value">{stats.spot !== undefined ? usd(spotNum) : "—"}</span>
          </Link>
          <Link href="/app" className="live-stat" aria-label="reserve in USDS, open the app">
            <span className="ls-label">reserve (USDS)</span>
            <span className="ls-value"><StatValue full={usdUnits(stats.reserve, 0)} short={usdCompactUnits(stats.reserve)} /></span>
          </Link>
          <Link href="/app" className="live-stat" aria-label="circulating supply, open the app">
            <span className="ls-label">circulating</span>
            <span className="ls-value"><StatValue full={fmtUnits(stats.minted, 0)} short={compactUnits(stats.minted)} /></span>
          </Link>
          <Link href="/app" className="live-stat" aria-label="trading volume since launch, in USDS, open the app">
            <span className="ls-label">volume (USDS)</span>
            <span className="ls-value"><StatValue full={usdUnits(volWei, 0)} short={usdCompactUnits(volWei)} /></span>
          </Link>
          <Link href="/app" className="live-stat" aria-label="01 burned and removed from supply, open the app">
            <span className="ls-label">burned</span>
            <span className="ls-value"><StatValue full={fmtUnits(stats.burned, 0)} short={compactUnits(stats.burned)} /></span>
          </Link>
        </>
      )}
      {stats.bonded === true ? (
        <Link
          href="/blog/bonding"
          className="pill pill-bonded"
          aria-label="the curve has bonded, read what changed and how to act"
        >
          bonded
        </Link>
      ) : stats.nearBond && stats.pctIssued !== undefined ? (
        <Link
          href="/blog/bonding"
          className="pill pill-issuing"
          aria-label={`issuing, ${Math.floor(stats.pctIssued)} percent of the sellable supply is minted, read what the bond changes`}
        >
          issuing · {Math.floor(stats.pctIssued)}% minted
        </Link>
      ) : (
        <Link href="/app" className="pill pill-issuing">issuing</Link>
      )}
    </div>
  );
}
